import Fetcher from "../fetcher";
import { parseChargeCallbacks } from "./utils";
import { USES_ORDER_REFERENCE } from "./flags";
import { callback, noop, isFunction, getValueAtPath } from "../utils";
import { traverseDataFields } from "./handler/helpers";
import { CHECKOUT_CLOSE_EVENT } from "../context/helpers/tokens";
import { DEFAULT_POST_INIT, DEFAULT_RETRY_CONDITIONS, isJsonResponse, withSharedHeaders } from "../fetcher/utils";
import { $successful } from "../context/payment";

const $fetcher = withSharedHeaders(Fetcher.one
  .limit(Infinity)
  .init(DEFAULT_POST_INIT)
  .retry(...DEFAULT_RETRY_CONDITIONS));
//120 polls at 1.5s intervals = 3 minutes of polling, which is the max time for pending transactions to resolve on the bank's end.
const ACCOUNT_PENDING_MAX_POLLS = 120;


let controller, signal;

function withCancellationListener () {
  const listener = cancellationListener.bind(this);
  
  addEventListener(CHECKOUT_CLOSE_EVENT, listener, false);
  return () => removeEventListener(CHECKOUT_CLOSE_EVENT, listener, false)
}

function cancellationListener () {
  const { $charge } = this;
  $charge.cancel();
}

function __refreshController__() {
  if (!signal || signal.aborted) {
    controller = new AbortController();
    ({ signal } = controller);
  }
}

function __getPollEndpoint__() {
  const type = this.$state.method.current;
  let POLL_ENDPOINT;

  if (type === 'enaira') {
    POLL_ENDPOINT = `/flwv3-pug/getpaidx/api/enaira/complete`
  } else if (type === 'capitec') {
    POLL_ENDPOINT = `flwv3-pug/getpaidx/api/checkout/transaction/requery`
  } else {
    POLL_ENDPOINT = `/flwv3-pug/getpaidx/api/verify/${
      type === 'banktransfer' && 'pwbt' ||
      type === 'payattitude' && 'payattitude' ||
      'mpesa'
    }`;
  }

  return new URL(POLL_ENDPOINT, process.env.API_BASE_URL);
}

export default function __poll__(reference, callbacks, paymentMethodContext) {
  __refreshController__();

  const $this = this;
  const isOrderReference = Boolean($this.flags & USES_ORDER_REFERENCE);
  const stopListeningForCancellation = withCancellationListener.call(paymentMethodContext);

  let pollingCount = 0;
  let maxPollingCount = Infinity;

  let {
    onBegin,
    onCancel,
    onComplete,
    onConfirming,
    onDataResponse,
    onError,
    onFailure,
    onSuccessful
  } = parseChargeCallbacks(callbacks);

  onBegin = callback(onBegin);
  onCancel = callback(onCancel);
  onComplete = callback(onComplete);
  onConfirming = callback(onConfirming);
  onDataResponse = callback(onDataResponse);
  onError = callback(onError, noop /* console.error */);
  onFailure = callback(onFailure);
  onSuccessful = callback(onSuccessful);

  const endpoint = __getPollEndpoint__.call($this);

  const payload = {
    modalauditid: $this.$chargeData.modalauditid,
    PBFPubKey: $this.$chargeData.PBFPubKey,
    [isOrderReference === true ? 'order_ref' : 'flw_ref']: reference
  };

  if ($this.$state.method.current === 'banktransfer') {
    payload.is_v3_verify = true;
  }

  if ($this.$state.method.current === 'capitec') {
    payload.payment_type = 'capitec'
    const pollStartTime = getValueAtPath(paymentMethodContext, '$$data.updatedAt') || null;
    payload.poll_start_time = pollStartTime;
  }

  const initOptions = {
    body: JSON.stringify(payload)
  };

  onBegin.call($this);
  
  function __complete__(responsedata) {
    return Promise.resolve(responsedata)
      .then(onComplete.bind($this));
  }

  (function __poll__() {
    if (signal.aborted) {
      pollingCount = 0;
      __refreshController__();
      stopListeningForCancellation();
      return Promise.resolve();
    }

    // A success view has already been shown this session — stop polling.
    // Must return a promise (not undefined) to keep the .catch() below valid.
    if ($successful) return Promise.resolve();

    return $fetcher(endpoint, initOptions)
      .then(async response => {
        if (isJsonResponse(response) && response.ok) {
          const responsedata = await response.json();

          const { status, chargeResponseCode, paymentType } =
            traverseDataFields(responsedata, 'status', 'chargeResponseCode', 'paymentType');

          // Trigger callback for data response.
          // /context/payment/helpers/charge/index.js => more context!
          onDataResponse(responsedata);

          if (
            ['00'].indexOf(chargeResponseCode) >= 0 ||
            ['success', 'successful'].indexOf(String(status).toLowerCase()) >= 0
          ) return (onSuccessful(responsedata), __complete__(responsedata));

          if (status === 'failed') return (onFailure(responsedata), __complete__(responsedata));

           if (chargeResponseCode === '02') {
           const pt = String(paymentType).toLowerCase();

           if (['cryptocurrency', 'account-ach-us'].indexOf(pt) >= 0)
             return (onConfirming(responsedata), __complete__(responsedata));


           // EU/UK ACH: keep polling until it resolves or the budget runs out,
           // then fall back to the pending / email page.
           if (['account-ach-uk', 'account-ach-eu'].indexOf(pt) >= 0) {
             if (++pollingCount >= ACCOUNT_PENDING_MAX_POLLS)
               return (onConfirming(responsedata), __complete__(responsedata));
             return setTimeout(() => __poll__(), 1500);
           }
         }
        }

        if (++pollingCount < maxPollingCount) {
          return setTimeout(() => __poll__(), 1500);
        }
        throw new RangeError("Too many polling attempts");
      }, __poll__)
  })().catch(onError);
  
  function __cancelPoll__(callback) {
    controller && controller.abort()
    isFunction(callback) && callback();
  };

  return __cancelPoll__.bind(null, onCancel);
}